/**
 * User Endpoint Getters
 * Type-safe path constructors for all user-facing API endpoints.
 */

import { getEndpoint } from '../url-builders';
import { API_ENDPOINTS } from './raw';

export const userEndpoints = {
  auth: {
    login: () => getEndpoint(API_ENDPOINTS.user.auth.login),
    logout: () => getEndpoint(API_ENDPOINTS.user.auth.logout),
    refresh: () => getEndpoint(API_ENDPOINTS.user.auth.refresh),
    register: () => getEndpoint(API_ENDPOINTS.user.auth.register),
    profile: () => getEndpoint(API_ENDPOINTS.user.auth.profile),
    anonymous: () => getEndpoint(API_ENDPOINTS.user.auth.anonymous),
    verifyEmail: () => getEndpoint(API_ENDPOINTS.user.auth.verifyEmail),
    checkEmail: () => getEndpoint(API_ENDPOINTS.auth.checkEmail),
    forgotPassword: () => getEndpoint(API_ENDPOINTS.user.auth.forgotPassword),
    verifyResetCode: () => getEndpoint(API_ENDPOINTS.user.auth.verifyResetCode),
    resetPassword: () => getEndpoint(API_ENDPOINTS.user.auth.resetPassword),
    verifyTwoFactor: () => getEndpoint(API_ENDPOINTS.user.auth.verifyTwoFactor),
    resendTwoFactor: () => getEndpoint(API_ENDPOINTS.user.auth.resendTwoFactor),
    emailVerificationStatus: () =>
      getEndpoint(API_ENDPOINTS.auth.emailVerificationStatus),
    resendVerificationEmail: () =>
      getEndpoint(API_ENDPOINTS.auth.resendVerificationEmail),
    // Email OTP (Primary auth method)
    otpSend: () => getEndpoint(API_ENDPOINTS.user.auth.otpSend),
    otpVerify: () => getEndpoint(API_ENDPOINTS.user.auth.otpVerify),
    // Passkey/WebAuthn (Secondary auth method)
    passkeyLoginOptions: () =>
      getEndpoint(API_ENDPOINTS.user.auth.passkeyLoginOptions),
    passkeyLoginVerify: () =>
      getEndpoint(API_ENDPOINTS.user.auth.passkeyLoginVerify),
    passkeyRegisterOptions: () =>
      getEndpoint(API_ENDPOINTS.user.auth.passkeyRegisterOptions),
    passkeyRegisterVerify: () =>
      getEndpoint(API_ENDPOINTS.user.auth.passkeyRegisterVerify),
    passkeys: () => getEndpoint(API_ENDPOINTS.user.auth.passkeys),
    passkeyDelete: (passkeyId: string) =>
      `${getEndpoint(API_ENDPOINTS.user.auth.passkeys)}/${passkeyId}`,
    passkeyRename: (passkeyId: string) =>
      `${getEndpoint(API_ENDPOINTS.user.auth.passkeys)}/${passkeyId}`,
    // Phone OTP (Account recovery fallback)
    phoneSend: () => getEndpoint(API_ENDPOINTS.user.auth.phoneSend),
    phoneVerify: () => getEndpoint(API_ENDPOINTS.user.auth.phoneVerify),
    // Email validation status (SSE endpoint)
    validationStatus: (jobId: string) =>
      `${getEndpoint(API_ENDPOINTS.user.auth.validationStatus)}?jobId=${jobId}`,
    social: () => getEndpoint(API_ENDPOINTS.user.auth.social)
  },
  profile: {
    get: () => getEndpoint(API_ENDPOINTS.user.profile.get),
    update: () => getEndpoint(API_ENDPOINTS.user.profile.update),
    completion: () => getEndpoint(API_ENDPOINTS.user.profile.completion),
    avatar: () => getEndpoint(API_ENDPOINTS.user.profile.avatar),
    organizationLogo: () =>
      getEndpoint(API_ENDPOINTS.user.profile.organizationLogo),
    verificationStatus: () =>
      getEndpoint(API_ENDPOINTS.user.profile.verificationStatus)
  },
  campaigns: () => getEndpoint(API_ENDPOINTS.user.campaigns),
  donations: {
    list: () => getEndpoint(API_ENDPOINTS.user.donations.list),
    statistics: () => getEndpoint(API_ENDPOINTS.user.donations.statistics),
    detail: (id: string) =>
      `${getEndpoint(API_ENDPOINTS.user.donations.detail)}/${id}`,
    receipt: (id: string) =>
      `${getEndpoint(API_ENDPOINTS.user.donations.receipt)}/${id}/receipt`
  },
  stats: () => getEndpoint(API_ENDPOINTS.user.stats),
  context: () => getEndpoint(API_ENDPOINTS.user.context),
  settings: () => getEndpoint(API_ENDPOINTS.user.settings),
  verification: {
    createSession: () => getEndpoint(API_ENDPOINTS.user.verification.session),
    cancelSession: () => getEndpoint(API_ENDPOINTS.user.verification.session), // DELETE /verification/session
    getStatus: () => getEndpoint(API_ENDPOINTS.user.verification.status),
    refreshStatus: () =>
      getEndpoint(API_ENDPOINTS.user.verification.statusRefresh),
    statusStream: () =>
      getEndpoint(API_ENDPOINTS.user.verification.statusStream), // SSE endpoint
    canCreateCampaigns: () =>
      getEndpoint(API_ENDPOINTS.user.verification.canCreateCampaigns),
    canDonate: () => getEndpoint(API_ENDPOINTS.user.verification.canDonate)
  },
  kyc: {
    profile: () => getEndpoint(API_ENDPOINTS.user.kyc.profile),
    submit: () => getEndpoint(API_ENDPOINTS.user.kyc.submit),
    documents: (documentType: string) =>
      `${getEndpoint(API_ENDPOINTS.user.kyc.documents)}?type=${encodeURIComponent(documentType)}`
  },
  payoutAccount: {
    get: () => getEndpoint(API_ENDPOINTS.user.payoutAccount.get),
    draft: () => getEndpoint(API_ENDPOINTS.user.payoutAccount.draft),
    submit: () => getEndpoint(API_ENDPOINTS.user.payoutAccount.submit),
    paypal: {
      get: () => getEndpoint(API_ENDPOINTS.user.payoutAccount.paypal.get),
      referral: () => getEndpoint(API_ENDPOINTS.user.payoutAccount.paypal.referral),
      returnCapture: (query: string) =>
        `${getEndpoint(API_ENDPOINTS.user.payoutAccount.paypal.return)}${query ? `?${query}` : ''}`,
      refreshStatus: () =>
        getEndpoint(API_ENDPOINTS.user.payoutAccount.paypal.refreshStatus),
      loa: () => getEndpoint(API_ENDPOINTS.user.payoutAccount.paypal.loa),
      loaFile: () => getEndpoint(API_ENDPOINTS.user.payoutAccount.paypal.loaFile)
    }
  },
  // Legal Consent endpoints (GDPR compliance)
  consent: {
    status: () => getEndpoint(API_ENDPOINTS.user.consent.status),
    history: () => getEndpoint(API_ENDPOINTS.user.consent.history),
    grant: () => getEndpoint(API_ENDPOINTS.user.consent.grant),
    withdraw: () => getEndpoint(API_ENDPOINTS.user.consent.withdraw)
  },

  notificationPreferences: {
    get: () => getEndpoint(API_ENDPOINTS.user.notificationPreferences.get),
    update: () =>
      getEndpoint(API_ENDPOINTS.user.notificationPreferences.update),
    marketingConsent: () =>
      getEndpoint(API_ENDPOINTS.user.notificationPreferences.marketingConsent),
    channels: () =>
      getEndpoint(API_ENDPOINTS.user.notificationPreferences.channels),
    modes: () => getEndpoint(API_ENDPOINTS.user.notificationPreferences.modes),
    health: () => getEndpoint(API_ENDPOINTS.user.notificationPreferences.health)
  },

  // User Notifications endpoints
  notifications: {
    list: () => getEndpoint(API_ENDPOINTS.notifications.list),
    unreadCount: () => getEndpoint(API_ENDPOINTS.notifications.unreadCount),
    markRead: (id: string) =>
      `${getEndpoint(API_ENDPOINTS.notifications.markRead)}/${id}/read`,
    markAllRead: () => getEndpoint(API_ENDPOINTS.notifications.markAllRead),
    dismiss: (id: string) =>
      `${getEndpoint(API_ENDPOINTS.notifications.dismiss)}/${id}`,
    stream: () => getEndpoint(API_ENDPOINTS.notifications.stream)
  }
} as const;
